import { ButtonBase, Popover, Stack, Typography } from '@mui/material';
import { useIsMobile } from '@side/pantry';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { useAtomValue, useSetAtom } from 'jotai';
import { isEqual } from 'lodash';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  activeViewAllGroup,
  anchoredPartnerCardId,
  hoveredTeamId,
  isActiveViewAllGroupFamily,
  mailingAddressMatchesSelectedLocationFamily,
  selectedGroupKey,
  selectedPartnerMapTeamId,
  teamDetailPanelVisible,
  viewAllTeamId,
} from '@/common/atoms/community';
import { setImmediate } from '@/common/utils/setImmediate';
import { TrackCommunityEvent } from '$analytics/useTrackAnalytics/domains/community/events';
import { useTrackCommunityAnalytics } from '$analytics/useTrackAnalytics/domains/community/useTrackCommunityAnalytics';
import { usePinClass } from '../../hooks/usePinClass';
import type { TeamSearchResult } from '../../types';
import { MapPin } from './MapPin';

interface Props {
  partner: TeamSearchResult;
  setMarker: (
    marker: google.maps.marker.AdvancedMarkerElement | null,
    key: string,
  ) => void;
}

export const Marker = memo(
  ({ partner, setMarker }: Props) => {
    const trackEvent = useTrackCommunityAnalytics();
    const isMobile = useIsMobile('md');
    const setTeamDetailsOpen = useSetAtom(teamDetailPanelVisible);
    const setSelectedTeamId = useSetAtom(selectedPartnerMapTeamId);
    const setSelectedGroup = useSetAtom(selectedGroupKey);
    const setAnchoredPartnerId = useSetAtom(anchoredPartnerCardId);
    const pinRef = useRef(null);
    const [isMobilePopupOpen, setIsMobilePopupOpen] = useState(false);
    const { id, legalName, mailingAddress, alias } = partner;
    const hoveredId = useAtomValue(hoveredTeamId);
    const isHovered = hoveredId === id;
    const selectedId = useAtomValue(selectedPartnerMapTeamId);
    const isPanelVisible = useAtomValue(teamDetailPanelVisible);
    const isTeamDetailsOpen = selectedId === id && isPanelVisible;
    const groupKey = `${mailingAddress.geolocationLatitude}_${mailingAddress.geolocationLongitude}`;
    const isActiveViewAllTeam = useAtomValue(
      isActiveViewAllGroupFamily(groupKey),
    );
    const mailingAddressMatches = useAtomValue(
      mailingAddressMatchesSelectedLocationFamily(partner.mailingAddress),
    );

    const pinClass = usePinClass({
      active: isHovered || isTeamDetailsOpen,
      selected: isTeamDetailsOpen || isMobilePopupOpen || isActiveViewAllTeam,
      address: mailingAddress,
    });

    const setMarkerRef = useCallback(
      (ref: google.maps.marker.AdvancedMarkerElement | null) =>
        setMarker(ref, groupKey),
      [setMarker, groupKey],
    );

    const handleHover = useCallback(
      () =>
        trackEvent(TrackCommunityEvent.PartnerMapPinHover, {
          eventTarget: 'team_pin',
          teamId: id,
          teamAddressId: mailingAddress.id,
          inBoundary: !pinClass.includes('outOfBounds'),
          geoValue: [
            mailingAddress.geolocationLatitude,
            mailingAddress.geolocationLongitude,
          ],
        }),
      [
        id,
        mailingAddress.geolocationLatitude,
        mailingAddress.geolocationLongitude,
        mailingAddress.id,
        pinClass,
        trackEvent,
      ],
    );

    const viewAllTeam = useAtomValue(viewAllTeamId);
    const setActiveViewAllGroup = useSetAtom(activeViewAllGroup);
    const selectTeam = useCallback(() => {
      setSelectedTeamId(id);
      setAnchoredPartnerId(id);
      setSelectedGroup(undefined);
      if (isMobile) {
        if (viewAllTeam) {
          setActiveViewAllGroup(groupKey);
        } else {
          setIsMobilePopupOpen(true);
        }
      } else {
        setTeamDetailsOpen(true);
      }
    }, [
      id,
      isMobile,
      groupKey,
      viewAllTeam,
      setActiveViewAllGroup,
      setSelectedTeamId,
      setAnchoredPartnerId,
      setSelectedGroup,
      setTeamDetailsOpen,
    ]);

    const handleClick = useCallback(() => {
      selectTeam();
      trackEvent(TrackCommunityEvent.PartnerMapTeamSelection, {
        eventTarget: 'team_pin',
        teamId: id,
        teamAddressId: mailingAddress.id,
        inBoundary: !pinClass.includes('outOfBounds'),
        geoValue: [
          mailingAddress.geolocationLatitude,
          mailingAddress.geolocationLongitude,
        ],
        teamOrder: null,
      });
    }, [
      selectTeam,
      mailingAddress.id,
      mailingAddress.geolocationLatitude,
      mailingAddress.geolocationLongitude,
      pinClass,
      id,
      trackEvent,
    ]);

    const [position] = useState({
      lat: mailingAddress.geolocationLatitude,
      lng: mailingAddress.geolocationLongitude,
    });

    useEffect(() => {
      if (mailingAddressMatches) {
        // Wait for pin to render before selecting to avoid popover shift
        setImmediate(() => requestAnimationFrame(selectTeam));
      }
    }, [mailingAddressMatches, selectTeam]);

    return (
      <AdvancedMarker
        position={position}
        onClick={handleClick}
        ref={setMarkerRef}
      >
        <Popover
          anchorEl={pinRef.current}
          open={isMobilePopupOpen}
          onClose={() => {
            setIsMobilePopupOpen(false);
            setSelectedTeamId(undefined);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          slotProps={{
            paper: {
              sx: {
                width: 260,
              },
            },
          }}
          sx={{ mt: 0.5 }}
        >
          <Stack
            component={ButtonBase}
            onClick={() => {
              setTeamDetailsOpen(true);
              setIsMobilePopupOpen(false);
            }}
            sx={{ gap: 0.5, alignItems: 'flex-start', p: 2, textAlign: 'left' }}
          >
            <Typography variant="h5" sx={{ color: 'primary.main' }}>
              {legalName ?? alias}
            </Typography>
            {legalName && alias && (
              <Typography
                variant="caption"
                sx={{ pt: 0.5, pb: 1, color: 'text.secondary' }}
              >
                {alias}
              </Typography>
            )}
            <Typography variant="body3">
              {mailingAddress.formattedAddress}
            </Typography>
          </Stack>
        </Popover>
        <MapPin
          className={pinClass}
          ref={pinRef}
          teamName={legalName}
          alias={alias}
          teamId={id}
          address={mailingAddress.formattedAddress}
          onHover={handleHover}
        />
      </AdvancedMarker>
    );
  },
  (prevProps, props) => isEqual(prevProps, props),
);

Marker.displayName = 'Marker';
